import React, { useEffect, useState } from "react";
import Nav from "../master/NavBar";
import Header from "../master/SideBar";
import { BranchStatusUpdate, getBranchData } from "../../api/BranchService";
import { branchAdd, branchUpdate, superAdminAdd } from "../../api/AdminService";
import { statesList, districtsList } from "../../api/Locations";
import TransferBranchLayout from "./TransferBranchLayout";

const emptyForm = {
  id: "",
  username: "",
  name: "",
  mobile: "",
  email: "",
  address: "",
  state: "",
  district: "",
  pincode: "",
  school_photo: null,
  school_logo: null,
};

const emptyAdminForm = {
  registration_no: "",
  name: "",
  father_name: "",
  mobile: "",
  gender: "Male",
  dob: "",
};

const basicFields = [
  { key: "username", label: "Username", required: true },
  { key: "name", label: "Branch Name", required: true },
  { key: "mobile", label: "Mobile", required: true, maxLength: 10, pattern: "[0-9]{10}", inputMode: "numeric" },
  { key: "email", label: "Email", type: "email" },
  { key: "address", label: "Address", wide: true },
];


const getErrorMessage = (error) => {
  const message = error?.response?.data?.message || error?.message || "Something went wrong";
  return typeof message === "string"
    ? message
    : Object.values(message).flat().join("\n");
};

export default function AllBranchList() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [mainBranch, setMainBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [modalMode, setModalMode] = useState(null); // add | edit
  const [form, setForm] = useState({ ...emptyForm });
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Super Admin modal
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminBranch, setAdminBranch] = useState(null);
  const [adminForm, setAdminForm] = useState({ ...emptyAdminForm });

  const activeCount = branches.filter((item) => Number(item.status) === 1).length;
  const inactiveCount = branches.filter((item) => Number(item.status) !== 1).length;

  const filteredBranches = branches.filter((item) => {
    const keyword = search.trim().toLowerCase();
    const matchesSearch = !keyword || [
      item.name, item.username, item.mobile, item.email, item.address,
      item.state, item.district
    ].filter(Boolean).join(" ").toLowerCase().includes(keyword);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && Number(item.status) === 1) ||
      (statusFilter === "inactive" && Number(item.status) !== 1);

    return matchesSearch && matchesStatus;
  });

  const fetchBranch = async () => {
    try {
      setLoading(true);
      const res = await getBranchData();
      setMainBranch(res?.data?.data?.main || null);
      const branchList = res?.data?.data?.branches || [];
      setBranches(branchList);
      return branchList;
    } catch (error) {
      console.error(error);
      window.alert(getErrorMessage(error));
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranch();
  }, []);

  useEffect(() => {
    if (!modalMode) return;
    let mounted = true;
    statesList()
      .then((res) => {
        if (mounted && res?.data?.status) setStates(res?.data?.data || []);
      })
      .catch((error) => console.error("State list error:", error));
    return () => {
      mounted = false;
    };
  }, [modalMode]);

  useEffect(() => {
    if (!modalMode) return;
    let mounted = true;
    setDistricts([]);
    const query = form.state ? `&state=${encodeURIComponent(form.state)}` : "";
    districtsList(query)
      .then((res) => {
        if (mounted && res?.data?.status) setDistricts(res?.data?.data || []);
      })
      .catch((error) => console.error("District list error:", error));
    return () => {
      mounted = false;
    };
  }, [modalMode, form.state]);

  useEffect(() => {
    if (!modalMode) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [modalMode]);

  const openAddModal = () => {
    setForm({ ...emptyForm });
    setModalMode("add");
  };

  const openEditModal = (branch) => {
    setForm({
      ...emptyForm,
      ...Object.fromEntries(
        Object.keys(emptyForm)
          .filter((key) => !["school_photo", "school_logo"].includes(key))
          .map((key) => [key, branch?.[key] ?? ""])
      ),
      id: branch.id,
      username: branch.username || "",
      school_photo: null,
      school_logo: null,
    });
    setModalMode("edit");
  };

  const closeModal = () => {
    if (saving) return;
    setModalMode(null);
    setForm({ ...emptyForm });
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "state" ? { district: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      const payload = new FormData();

      if (modalMode === "edit") payload.append("id", form.id);
      payload.append("new_username", (form.username || "").trim());

      Object.keys(emptyForm).forEach((key) => {
        if (["id", "username", "school_photo", "school_logo"].includes(key)) return;
        payload.append(key, form[key] ?? "");
      });

      if (form.school_photo instanceof File) payload.append("school_photo", form.school_photo);
      if (form.school_logo instanceof File) payload.append("school_logo", form.school_logo);

      const response = modalMode === "edit"
        ? await branchUpdate(payload)
        : await branchAdd(payload);

      if (!response?.data?.status) {
        throw new Error(getErrorMessage({ response: { data: response?.data } }));
      }

      window.alert(response?.data?.message || "Branch saved successfully");

      const wasAddingBranch = modalMode === "add";
      const submittedUsername = (form.username || "").trim();
      const createdData = response?.data?.data || {};
      let createdBranchId =
        createdData?.id ||
        createdData?.client_id ||
        response?.data?.id ||
        null;

      setModalMode(null);
      setForm({ ...emptyForm });

      const refreshedBranches = await fetchBranch();

      // After creating a branch, open the Super Admin form for that branch.
      if (wasAddingBranch) {
        let newBranch = createdBranchId
          ? refreshedBranches.find((item) => String(item.id) === String(createdBranchId))
          : null;

        if (!newBranch) {
          newBranch = refreshedBranches.find(
            (item) => String(item.username || "").trim() === submittedUsername
          );
        }

        if (newBranch) {
          openAdminModal(newBranch);
        } else if (createdBranchId) {
          openAdminModal({ id: createdBranchId, name: createdData?.name || "New Branch" });
        } else {
          window.alert("Branch added. Please use the user-add icon in Actions to add its Super Admin.");
        }
      }
    } catch (error) {
      console.error(error);
      window.alert(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openAdminModal = (branch) => {
    setAdminBranch(branch);
    setAdminForm({ ...emptyAdminForm });
    setAdminModalOpen(true);
  };

  const closeAdminModal = () => {
    if (adminSaving) return;
    setAdminModalOpen(false);
    setAdminBranch(null);
    setAdminForm({ ...emptyAdminForm });
  };

  const updateAdminField = (key, value) => {
    setAdminForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (adminSaving || !adminBranch?.id) return;

    try {
      setAdminSaving(true);

      const payload = {
        id: adminBranch.id,
        registration_no: adminForm.registration_no.trim(),
        name: adminForm.name.trim(),
        father_name: adminForm.father_name.trim(),
        mobile: adminForm.mobile,
        gender: adminForm.gender,
        dob: adminForm.dob || null,
      };

      const response = await superAdminAdd(payload);

      if (!response?.data?.status) {
        throw new Error(getErrorMessage({ response: { data: response?.data } }));
      }

      window.alert(response?.data?.message || "Super Admin added successfully");
      setAdminModalOpen(false);
      setAdminBranch(null);
      setAdminForm({ ...emptyAdminForm });
      await fetchBranch();
    } catch (error) {
      console.error(error);
      window.alert(getErrorMessage(error));
    } finally {
      setAdminSaving(false);
    }
  };

  const handleStatusChange = async (branch, newStatus) => {
    try {
      setStatusLoadingId(branch.id);
      const response = await BranchStatusUpdate(branch.id, newStatus);
      if (response?.data?.status === false) {
        throw new Error(getErrorMessage({ response: { data: response.data } }));
      }
      setBranches((prev) => prev.map((item) =>
        item.id === branch.id ? { ...item, status: newStatus } : item
      ));
    } catch (error) {
      console.error(error);
      window.alert(getErrorMessage(error));
    } finally {
      setStatusLoadingId(null);
    }
  };

  return (
    <>
      <style>{`
        .branch-page { color: #505560; }
        .branch-hero {
          position: relative; overflow: hidden; border: 1px solid #e5e7ee;
          border-radius: 15px; background: #fff;
        }
        .branch-hero:after {
          content: ""; position: absolute; width: 220px; height: 220px;
          right: -65px; top: -105px; border-radius: 50%;
          background: rgba(105,108,255,.07); pointer-events: none;
        }
        .branch-hero-main { display:flex; align-items:center; gap:15px; min-width:0; }
        .branch-main-icon {
          width:54px;height:54px;flex:0 0 54px;display:flex;align-items:center;
          justify-content:center;border-radius:14px;background:#eef0ff;color:#696cff;font-size:25px;
        }
        .branch-eyebrow { color:#9297a2;font-size:10px;font-weight:800;letter-spacing:.09em;text-transform:uppercase; }
        .branch-hero h4 { color:#414650;font-size:20px;font-weight:800;margin:3px 0 5px; }
        .branch-hero p { color:#9297a2;font-size:12px;margin:0; }
        .branch-stat-grid { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:15px 0; }
        .branch-stat {
          display:flex;align-items:center;gap:12px;padding:15px;border:1px solid #e7e9ef;
          border-radius:12px;background:#fff;min-width:0;
        }
        .branch-stat-icon { width:42px;height:42px;display:flex;align-items:center;justify-content:center;
          flex:0 0 42px;border-radius:11px;font-size:19px; }
        .branch-stat-icon.purple {background:#eef0ff;color:#696cff}
        .branch-stat-icon.green {background:#e8f8ef;color:#278e55}
        .branch-stat-icon.red {background:#ffeded;color:#d34849}
        .branch-stat small {display:block;color:#9297a2;font-size:10px;font-weight:800;text-transform:uppercase}
        .branch-stat strong {display:block;margin-top:3px;color:#414650;font-size:23px;line-height:1.1}
        .branch-card {overflow:hidden;border:1px solid #e1e4ea;border-radius:14px;background:#fff}
        .branch-card-head {display:flex;align-items:center;justify-content:space-between;gap:15px;padding:16px;border-bottom:1px solid #e9ebef}
        .branch-card-head h5 {margin:0;color:#414650;font-size:17px;font-weight:800}
        .branch-card-head p {margin:4px 0 0;color:#9297a2;font-size:11px}
        .branch-tools {display:flex;align-items:center;gap:8px}
        .branch-search {position:relative}
        .branch-search i {position:absolute;left:11px;top:50%;transform:translateY(-50%);color:#9297a2}
        .branch-search input {height:39px;width:240px;padding:7px 12px 7px 33px;border:1px solid #dfe2e9;border-radius:8px;font-size:12px;outline:none}
        .branch-search input:focus,.branch-filter:focus {border-color:#8e91ff;box-shadow:0 0 0 3px rgba(105,108,255,.08)}
        .branch-filter {height:39px;min-width:130px;padding:6px 10px;border:1px solid #dfe2e9;border-radius:8px;background:#fff;color:#555a64;font-size:12px}
        .branch-table-wrap {width:100%;overflow:auto;max-height:68vh}
        .branch-table {width:100%;min-width:950px;border-collapse:separate;border-spacing:0}
        .branch-table th {position:sticky;top:0;z-index:2;padding:12px 11px;background:#f5f6f8;color:#626772;border-bottom:1px solid #dfe2e8;font-size:11px;font-weight:800;text-transform:uppercase;white-space:nowrap}
        .branch-table td {padding:12px 11px;border-bottom:1px solid #eceef2;background:#fff;color:#555a64;font-size:12px;vertical-align:middle}
        .branch-table tbody tr:hover td {background:#fafbfc}
        .branch-identity {display:flex;align-items:center;gap:10px;min-width:190px}
        .branch-avatar {width:38px;height:38px;flex:0 0 38px;display:flex;align-items:center;justify-content:center;border-radius:11px;background:#eef0ff;color:#696cff;font-size:12px;font-weight:900}
        .branch-identity strong {display:block;color:#414650;font-size:12px}
        .branch-identity small,.branch-subtext {display:block;margin-top:3px;color:#9297a2;font-size:10px}
        .branch-status {display:inline-flex;align-items:center;gap:5px;padding:5px 9px;border-radius:20px;font-size:10px;font-weight:800}
        .branch-status.active {background:#e8f8ef;color:#278e55}
        .branch-status.inactive {background:#ffeded;color:#d34849}
        .branch-status:before {content:"";width:6px;height:6px;border-radius:50%;background:currentColor}
        .branch-select {height:32px;min-width:115px;padding:4px 25px 4px 8px;border-radius:7px;font-size:11px}
        .branch-edit-btn {height:32px;padding:5px 10px;border:1px solid #dfe1ff;border-radius:7px;background:#f4f4ff;color:#696cff;font-size:11px;font-weight:800}
        .branch-edit-btn:hover {background:#696cff;color:#fff}
        .branch-action-icon {width:34px;height:34px;display:inline-flex;align-items:center;justify-content:center;
          border:1px solid #e0e3eb;border-radius:8px;background:#fff;color:#696cff;font-size:17px;transition:.15s}
        .branch-action-icon:hover {background:#696cff;border-color:#696cff;color:#fff}
        .branch-action-icon.user-add {color:#278e55;border-color:#d8eee0}
        .branch-action-icon.user-add:hover {background:#278e55;border-color:#278e55;color:#fff}
        .admin-modal {width:min(100%,620px)}
        .branch-empty {padding:55px 15px!important;text-align:center!important}
        .branch-empty i {display:block;margin-bottom:8px;color:#a3a7b0;font-size:32px}
        .branch-empty strong {display:block;color:#555a64;font-size:14px}
        .branch-empty span {display:block;margin-top:4px;color:#9297a2;font-size:11px}
        .branch-table-foot {display:flex;justify-content:space-between;gap:10px;padding:11px 15px;background:#fafbfc;border-top:1px solid #e7e9ee;color:#888e99;font-size:11px}
        .branch-modal-backdrop {position:fixed;inset:0;z-index:99999;display:flex;align-items:flex-start;justify-content:center;padding:18px 18px 18px;background:rgba(30,34,48,.58);backdrop-filter:blur(3px)}
        .branch-modal {width:min(100%,1000px);max-height:calc(100vh - 36px);display:flex;flex-direction:column;overflow:hidden;border:0;border-radius:16px;background:#fff;box-shadow:0 20px 65px rgba(20,25,45,.25)}
        .branch-modal-head {display:flex;align-items:center;justify-content:space-between;padding:18px 22px;background:#fff;border-bottom:1px solid #eceef2}
        .branch-modal-head h5 {margin:0;color:#414650;font-size:17px;font-weight:800}
        .branch-modal-head p {margin:4px 0 0;color:#9297a2;font-size:11px}
        .branch-modal-icon {width:42px;height:42px;display:flex;align-items:center;justify-content:center;border-radius:12px;background:#eef0ff;color:#696cff;font-size:20px}
        .branch-modal-body {overflow:auto;padding:20px 22px}
        .branch-form-section {padding:15px;border:1px solid #e9ebf0;border-radius:12px;background:#fff}
        .branch-form-section + .branch-form-section {margin-top:14px}
        .branch-section-title {display:flex;align-items:center;gap:8px;margin:0 0 14px;color:#505560;font-size:12px;font-weight:900}
        .branch-section-title i {color:#696cff;font-size:17px}
        .branch-modal .form-label {margin-bottom:6px;color:#656a75;font-size:11px;font-weight:800}
        .branch-modal .form-control,.branch-modal .form-select {min-height:40px;border-color:#e0e3e9;border-radius:8px;font-size:12px}
        .branch-modal .form-control:focus,.branch-modal .form-select:focus {border-color:#8e91ff;box-shadow:0 0 0 3px rgba(105,108,255,.09)}
        .branch-modal-foot {display:flex;justify-content:flex-end;gap:8px;padding:14px 22px;border-top:1px solid #eceef2;background:#fafbfc}
        .branch-modal-foot .btn {min-width:105px;border-radius:8px;font-size:12px;font-weight:800}
        @media(max-width:767px) {
          .branch-stat-grid {grid-template-columns:1fr 1fr}
          .branch-stat:last-child {grid-column:span 2}
          .branch-card-head {align-items:stretch;flex-direction:column}
          .branch-tools {align-items:stretch;flex-direction:column}
          .branch-search input,.branch-filter {width:100%}
          .branch-hero-main {align-items:flex-start}
          .branch-modal-backdrop {padding:8px;align-items:flex-start}
          .branch-modal {max-height:calc(100vh - 16px);border-radius:12px}
          .branch-modal-head,.branch-modal-body,.branch-modal-foot {padding:14px}
          .branch-form-section {padding:12px}
        }
      `}</style>

      {loading && (
        <div className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9999, background: "rgba(255,255,255,.45)" }}>
          <div className="sk-chase sk-primary display-1">
            {Array.from({ length: 6 }).map((_, index) => <div className="sk-chase-dot" key={index} />)}
          </div>
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar branch-page">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <TransferBranchLayout />

                <section className="branch-hero mt-3">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                      <div className="branch-hero-main">
                        <div className="branch-main-icon"><i className="ri-building-4-line" /></div>
                        <div>
                          <div className="branch-eyebrow">Branch Management / Overview</div>
                          <h4>{mainBranch?.name || "Main Branch"}</h4>
                          <p><i className="ri-map-pin-line me-1" />{mainBranch?.address || "Address not available"}{mainBranch?.pincode ? ` • ${mainBranch.pincode}` : ""}</p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <span className={`branch-status ${Number(mainBranch?.status) === 1 ? "active" : "inactive"}`}>
                          {Number(mainBranch?.status) === 1 ? "Main Branch Active" : "Main Branch Inactive"}
                        </span>
                        <button type="button" className="btn btn-primary btn-sm px-3 py-2" onClick={openAddModal}>
                          <i className="ri-add-line me-1" /> Add New Branch
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="branch-stat-grid">
                  <div className="branch-stat">
                    <span className="branch-stat-icon purple"><i className="ri-building-line" /></span>
                    <div><small>Total Sub Branches</small><strong>{branches.length}</strong></div>
                  </div>
                  <div className="branch-stat">
                    <span className="branch-stat-icon green"><i className="ri-checkbox-circle-line" /></span>
                    <div><small>Active Branches</small><strong>{activeCount}</strong></div>
                  </div>
                  <div className="branch-stat">
                    <span className="branch-stat-icon red"><i className="ri-pause-circle-line" /></span>
                    <div><small>Inactive Branches</small><strong>{inactiveCount}</strong></div>
                  </div>
                </section>

                <section className="branch-card">
                  <div className="branch-card-head">
                    <div>
                      <h5><i className="ri-git-branch-line me-2 text-primary" />Sub Branch Directory</h5>
                      <p>Manage branch information, account status and branch details.</p>
                    </div>
                    <div className="branch-tools">
                      <div className="branch-search">
                        <i className="ri-search-line" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search name, username, mobile..." />
                      </div>
                      <select className="branch-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="branch-table-wrap">
                    <table className="branch-table">
                      <thead>
                        <tr>
                          <th style={{ width: 55 }}>#</th>
                          <th>Branch</th>
                          <th>Username</th>
                          <th>Contact</th>
                          <th>Email</th>
                          <th>Location</th>
                          <th>Status</th>
                          <th style={{ width: 105 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBranches.length > 0 ? filteredBranches.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="branch-identity">
                                <div className="branch-avatar">{String(item.name || "B").trim().split(/\s+/).slice(0, 2).map((word) => word[0]?.toUpperCase()).join("")}</div>
                                <div><strong>{item.name || "-"}</strong><small>Branch ID: #{item.id}</small></div>
                              </div>
                            </td>
                            <td><strong>{item.username || "-"}</strong></td>
                            <td>{item.mobile || "-"}</td>
                            <td>{item.email || "-"}</td>
                            <td>
                              <span>{item.district || item.address || "-"}</span>
                              {item.state && <small className="branch-subtext">{item.state}{item.pincode ? ` - ${item.pincode}` : ""}</small>}
                            </td>
                            <td>
                              <select
                                className={`form-select form-select-sm branch-select ${Number(item.status) === 1 ? "border-success" : "border-danger"}`}
                                value={item.status}
                                disabled={statusLoadingId === item.id}
                                onChange={(e) => handleStatusChange(item, Number(e.target.value))}
                              >
                                <option value={1}>Active</option>
                                <option value={2}>Inactive</option>
                              </select>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  className="branch-action-icon"
                                  title="Edit Branch"
                                  aria-label="Edit Branch"
                                  onClick={() => openEditModal(item)}
                                >
                                  <i className="ri-edit-line" />
                                </button>
                                <button
                                  type="button"
                                  className="branch-action-icon user-add"
                                  title="Add Super Admin"
                                  aria-label="Add Super Admin"
                                  onClick={() => openAdminModal(item)}
                                >
                                  <i className="ri-user-add-line" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="8" className="branch-empty">
                            <i className="ri-inbox-line" />
                            <strong>{branches.length ? "No matching branches found" : "No branches added yet"}</strong>
                            <span>{branches.length ? "Try changing your search or status filter." : "Use Add New Branch to create your first sub branch."}</span>
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="branch-table-foot">
                    <span>Showing <strong>{filteredBranches.length}</strong> of <strong>{branches.length}</strong> branches</span>
                    <span><i className="ri-shield-check-line me-1" />Branch status can be changed directly from this list</span>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalMode && (
        <div className="branch-modal-backdrop" onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}>
          <div className="branch-modal" role="dialog" aria-modal="true" aria-labelledby="branchModalTitle">
            <div className="branch-modal-head">
              <div className="d-flex align-items-center gap-3">
                <div className="branch-modal-icon"><i className={modalMode === "add" ? "ri-add-circle-line" : "ri-edit-2-line"} /></div>
                <div>
                  <h5 id="branchModalTitle">{modalMode === "add" ? "Create New Branch" : "Update Branch Details"}</h5>
                  <p>{modalMode === "add" ? "Add a new branch to your organization." : `Editing ${form.name || "selected branch"} • ID #${form.id}`}</p>
                </div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" disabled={saving} onClick={closeModal} />
            </div>

            <form onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
              <div className="branch-modal-body">
                <section className="branch-form-section">
                  <h6 className="branch-section-title"><i className="ri-building-4-line" />Branch Basic Information</h6>
                  <div className="row g-3">
                    {basicFields.map((field) => (
                      <div className={field.wide ? "col-12" : "col-md-6"} key={field.key}>
                        <label className="form-label">{field.label}{field.required && <span className="text-danger"> *</span>}</label>
                        <input type={field.type || "text"} className="form-control"
                          value={form[field.key] ?? ""} required={!!field.required}
                          maxLength={field.maxLength} pattern={field.pattern} inputMode={field.inputMode}
                          onChange={(e) => updateField(field.key, e.target.value)} />
                      </div>
                    ))}
                  </div>
                </section>

                <section className="branch-form-section">
                  <h6 className="branch-section-title"><i className="ri-map-pin-line" />Address & Location</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">State</label>
                      <select className="form-select" value={form.state || ""} onChange={(e) => updateField("state", e.target.value)}>
                        <option value="">Select State</option>
                        {form.state && !states.some((state) => state.name === form.state) && <option value={form.state}>{form.state}</option>}
                        {states.map((state, index) => <option key={index} value={state.name}>{state.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">District</label>
                      <select className="form-select" value={form.district || ""} onChange={(e) => updateField("district", e.target.value)}>
                        <option value="">Select District</option>
                        {form.district && !districts.some((district) => district.name === form.district) && <option value={form.district}>{form.district}</option>}
                        {districts.map((district, index) => <option key={index} value={district.name}>{district.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Pincode</label>
                      <input type="text" className="form-control" value={form.pincode || ""}
                        maxLength={6} inputMode="numeric" pattern="[0-9]{6}"
                        onChange={(e) => updateField("pincode", e.target.value)} />
                    </div>
                  </div>
                </section>

                <section className="branch-form-section">
                  <h6 className="branch-section-title"><i className="ri-image-line" />Institute Photo & Logo</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Institute Photo</label>
                      <input type="file" className="form-control" accept="image/jpeg,image/png,image/gif"
                        onChange={(e) => updateField("school_photo", e.target.files?.[0] || null)} />
                      {modalMode === "edit" && <small className="text-muted d-block mt-1">Leave empty to keep the existing photo.</small>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Institute Logo</label>
                      <input type="file" className="form-control" accept="image/jpeg,image/png,image/gif"
                        onChange={(e) => updateField("school_logo", e.target.files?.[0] || null)} />
                      {modalMode === "edit" && <small className="text-muted d-block mt-1">Leave empty to keep the existing logo.</small>}
                    </div>
                  </div>
                </section>
              </div>

              <div className="branch-modal-foot">
                <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</> :
                    <><i className={`me-1 ${modalMode === "add" ? "ri-add-line" : "ri-save-line"}`} />{modalMode === "add" ? "Create Branch" : "Save Changes"}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {adminModalOpen && (
        <div
          className="branch-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAdminModal();
          }}
        >
          <div className="branch-modal admin-modal" role="dialog" aria-modal="true" aria-labelledby="adminModalTitle">
            <div className="branch-modal-head">
              <div className="d-flex align-items-center gap-3">
                <div className="branch-modal-icon"><i className="ri-user-add-line" /></div>
                <div>
                  <h5 id="adminModalTitle">Add Branch Super Admin</h5>
                  <p>{adminBranch?.name || "Branch"} • Branch ID: #{adminBranch?.id}</p>
                </div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" disabled={adminSaving} onClick={closeAdminModal} />
            </div>

            <form onSubmit={handleAdminSubmit} className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
              <div className="branch-modal-body">
                <section className="branch-form-section">
                  <h6 className="branch-section-title"><i className="ri-user-3-line" />Super Admin Information</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Registration No. <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        value={adminForm.registration_no}
                        onChange={(e) => updateAdminField("registration_no", e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Mobile <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        className="form-control"
                        required
                        maxLength={10}
                        minLength={10}
                        pattern="[0-9]{10}"
                        inputMode="numeric"
                        value={adminForm.mobile}
                        onChange={(e) => updateAdminField("mobile", e.target.value.replace(/\\D/g, "").slice(0, 10))}
                      />
                      <small className="text-muted">Default password will be this mobile number.</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" required value={adminForm.name}
                        onChange={(e) => updateAdminField("name", e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Father Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" required value={adminForm.father_name}
                        onChange={(e) => updateAdminField("father_name", e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Gender <span className="text-danger">*</span></label>
                      <select className="form-select" required value={adminForm.gender}
                        onChange={(e) => updateAdminField("gender", e.target.value)}>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date of Birth</label>
                      <input type="date" className="form-control" value={adminForm.dob}
                        onChange={(e) => updateAdminField("dob", e.target.value)} />
                    </div>
                  </div>
                </section>
              </div>

              <div className="branch-modal-foot">
                <button type="button" className="btn btn-outline-secondary" disabled={adminSaving} onClick={closeAdminModal}>Skip</button>
                <button type="submit" className="btn btn-primary px-4" disabled={adminSaving}>
                  {adminSaving
                    ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
                    : <><i className="ri-user-add-line me-1" />Add Super Admin</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
